import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { shadeHexColor } from '@/utils';

import { useBreakpoints, useFormatters } from '@/hooks';

interface BaseIndicatorProps {
  value: number | null;
  sublabel: string;
  color: string;
  icon: React.ReactElement;
  loading?: boolean;
  format?: 'number' | 'currency';
}

const iconSize = {
  xs: 32,
  sm: 48,
  md: 48,
  lg: 72,
  xl: 72,
};

function BaseIndicator(props: BaseIndicatorProps) {
  const { sublabel, color, icon, loading, format } = props;

  const { isMobileDevice, useUp } = useBreakpoints();
  const upLg = useUp('lg');

  const { formatNumber, formatCurrency } = useFormatters();

  const value =
    props.value !== null
      ? format === 'currency'
        ? formatCurrency(props.value)
        : formatNumber(props.value)
      : '-';

  return (
    <Stack
      direction="row"
      spacing={!isMobileDevice ? 2 : 1}
      sx={{
        backgroundColor: shadeHexColor(color, 0.87),
        borderColor: shadeHexColor(color, 0.7),
        borderRadius: 4,
        borderWidth: 1,
        p: {
          xs: 1,
          sm: 1,
          md: 1,
          lg: 2,
          xl: 2,
        },
      }}
      component={Paper}
      variant="outlined"
    >
      {isMobileDevice ? (
        <Stack
          flexGrow={1}
          spacing={0}
          direction="column"
          alignItems="center"
          sx={{ overflow: 'hidden' }}
        >
          {loading ? (
            /* take as much space as Typography */
            <Box sx={{ width: 32, height: 32, p: 0.5 }}>
              <CircularProgress size={'100%'} />
            </Box>
          ) : (
            <Typography
              variant="h6"
              color={color}
              fontWeight="bold"
              noWrap
              sx={{ maxWidth: '100%' }}
            >
              {value}
            </Typography>
          )}
          <Typography
            color={color}
            noWrap
            fontSize={12}
            sx={{ maxWidth: '100%' }}
          >
            {sublabel}
          </Typography>
        </Stack>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignSelf: 'center',
              '& .MuiSvgIcon-root': {
                width: iconSize,
                height: iconSize,
              },
            }}
          >
            {loading ? (
              <Box sx={{ width: iconSize, height: iconSize, p: 1.5 }}>
                <CircularProgress size={'100%'} sx={{ color }} />
              </Box>
            ) : (
              icon
            )}
          </Box>
          <Stack
            flexGrow={1}
            spacing={0}
            direction="column"
            alignItems="flex-start"
            sx={{ overflow: 'hidden' }}
          >
            <Typography
              variant={upLg ? 'h4' : 'h5'}
              color={color}
              fontWeight="bold"
              // noWrap
              sx={{ maxWidth: '100%' }}
            >
              {value}
            </Typography>
            <Typography
              variant={upLg ? 'h5' : 'subtitle1'}
              color={color}
              noWrap
              sx={{ maxWidth: '100%' }}
            >
              {sublabel}
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
}

export { BaseIndicator };
export type { BaseIndicatorProps };
